import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { sileo } from "sileo";
import { api } from "@/lib/api-client";
import { useAuthStore } from "@/store/auth-store";
import { useAppStore } from "@/store/store";
import { fromApiTable, toApiTableCreate, toApiTableUpdate } from "@/lib/adapters";
import type { ApiTable, PaginatedResponse } from "@/types/api";
import type { Table } from "@/store/types";

// Backend is the single source of truth for error messages: ninja serialises
// every HttpError as ``{ detail: "..." }``. ky pre-parses the body into
// ``error.data`` (and consumes the stream doing so — see ky's HTTPError.js
// docstring), so reading ``error.data.detail`` is the only path that works.
function showApiError(error: unknown): void {
  const data = (error as { data?: { detail?: unknown } }).data;
  const detail = data?.detail;
  if (typeof detail === "string" && detail.length > 0) {
    sileo.error({ description: detail });
    return;
  }
  // Network failure or non-JSON body — fall back so the user sees something.
  sileo.error({ description: "Error de conexión. Intentá de nuevo." });
}

const TABLE_FIELDS = [
  "id",
  "zone_id",
  "name",
  "min_capacity",
  "max_capacity",
  "shape",
  "layout",
  "neighbors",
  "features",
  "is_available",
] as const satisfies readonly (keyof ApiTable)[];

// ── Queries ──────────────────────────────────────────────────────

export function useTables(locationId?: string, zoneId?: string) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const organizationId = useAuthStore((s) => s.organizationId);
  const storeLocationId = useAuthStore((s) => s.locationId);

  const query = useQuery({
    queryKey: ["tables", organizationId, locationId ?? storeLocationId, zoneId],
    queryFn: async () => {
      const params = new URLSearchParams({ fields: TABLE_FIELDS.join(",") });
      if (locationId) params.set("location", locationId);
      if (zoneId) params.set("zone", zoneId);
      const res = await api.get(`tables/?${params}`).json<PaginatedResponse<ApiTable>>();
      return res.items.map(fromApiTable);
    },
    enabled: isAuthenticated && !!organizationId && !!storeLocationId,
  });

  // Sync to store so derived helpers (getTableStatus, findTableCombinations) keep working.
  // ``dataUpdatedAt`` is part of the deps because React Query's structural sharing returns
  // the same ``data`` reference when a refetch produces deep-equal results — without it,
  // a 409 (rejected mutation, server state unchanged) leaves the optimistic store update
  // in place forever, even though invalidate triggered a fresh fetch.
  useEffect(() => {
    if (query.data) {
      useAppStore.setState({ tables: query.data });
    }
  }, [query.data, query.dataUpdatedAt]);

  return query;
}

// ── Mutations ────────────────────────────────────────────────────

export function useCreateTable() {
  const queryClient = useQueryClient();
  const organizationId = useAuthStore((s) => s.organizationId);

  return useMutation({
    mutationFn: (table: Table) =>
      api.post("tables/", { json: toApiTableCreate(table) }).json<ApiTable>(),
    onSuccess: (apiTable, localTable) => {
      // Replace local table with API version (correct UUID)
      const mapped = fromApiTable(apiTable);
      useAppStore.setState((state) => ({
        tables: state.tables.map((t) => (t.id === localTable.id ? mapped : t)),
      }));
      queryClient.invalidateQueries({ queryKey: ["tables", organizationId] });
    },
    onError: (error) => {
      showApiError(error);
      queryClient.invalidateQueries({ queryKey: ["tables", organizationId] });
    },
  });
}

export function useUpdateTable() {
  const queryClient = useQueryClient();
  const organizationId = useAuthStore((s) => s.organizationId);

  return useMutation({
    mutationFn: ({ id, changes }: { id: string; changes: Partial<Table> }) =>
      api.patch(`tables/${id}/`, { json: toApiTableUpdate(changes) }).json<ApiTable>(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tables", organizationId] });
    },
    onError: (error) => {
      showApiError(error);
      // Refetch so the optimistic store update rolls back to server state.
      queryClient.invalidateQueries({ queryKey: ["tables", organizationId] });
    },
  });
}

export function useDeleteTable() {
  const queryClient = useQueryClient();
  const organizationId = useAuthStore((s) => s.organizationId);

  return useMutation({
    mutationFn: (id: string) => api.delete(`tables/${id}/`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tables", organizationId] });
    },
  });
}

export function useToggleNeighbor() {
  const queryClient = useQueryClient();
  const organizationId = useAuthStore((s) => s.organizationId);

  return useMutation({
    mutationFn: async ({
      tableId,
      neighborId,
      tableNeighbors,
      neighborNeighbors,
    }: {
      tableId: string;
      neighborId: string;
      tableNeighbors: string[];
      neighborNeighbors: string[];
    }) => {
      // Run sequentially to avoid partial inconsistency
      await api
        .patch(`tables/${tableId}/`, { json: { neighbors: tableNeighbors } })
        .json<ApiTable>();
      await api
        .patch(`tables/${neighborId}/`, { json: { neighbors: neighborNeighbors } })
        .json<ApiTable>();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tables", organizationId] });
    },
    onError: (error) => {
      showApiError(error);
      // The optimistic toggle ran in the store before the mutation; if the
      // backend rejected (e.g. cross-zone), refetch so both ends re-sync.
      queryClient.invalidateQueries({ queryKey: ["tables", organizationId] });
    },
  });
}
